import { forwardRef, useEffect, useRef } from "react";
import { X } from "lucide-react";

/** Frosted card. `as` so a card can be a button without nesting one.
 *  forwardRef because the offer cards need a real node for the impression
 *  observer, and a component that swallows its ref fails silently. */
export const Glass = forwardRef(function Glass(
  { as: Tag = "div", tint, strong, className = "", ...rest }, ref
) {
  return (
    <Tag
      ref={ref}
      className={["glass", tint && "tint", strong && "strong", className].filter(Boolean).join(" ")}
      {...rest}
    />
  );
});

/** Neumorphic surface. `sunken` for inputs, tracks and pressed states. */
export const Neu = forwardRef(function Neu(
  { as: Tag = "div", sunken, className = "", ...rest }, ref
) {
  return <Tag ref={ref} className={[sunken ? "neu-in" : "neu", className].filter(Boolean).join(" ")} {...rest} />;
});

export function Pill({ tone, children }) {
  return <span className={["pill", tone].filter(Boolean).join(" ")}>{children}</span>;
}

export function Err({ children, onDismiss }) {
  if (!children) return null;
  return (
    <div className="err" role="alert">
      <span style={{ flex: 1 }}>{children}</span>
      {onDismiss && (
        <button onClick={onDismiss} aria-label="Dismiss">
          <X size={15} />
        </button>
      )}
    </div>
  );
}

export function Skeleton({ h = 72, w = "100%", r }) {
  return <div className="skel" style={{ height: h, width: w, borderRadius: r }} aria-hidden="true" />;
}

export function Empty({ icon: Icon, title, note, action }) {
  return (
    <div className="center">
      {Icon && <Icon size={34} strokeWidth={1.3} color="var(--mauve)" />}
      <div>
        <p style={{ margin: 0, fontFamily: "var(--font-bold)", fontWeight: 600 }}>{title}</p>
        {note && <p className="muted" style={{ margin: ".3rem 0 0", fontSize: ".85rem" }}>{note}</p>}
      </div>
      {action}
    </div>
  );
}

/**
 * Sheet — the portal's one navigation primitive. Everything that is not Home
 * opens over Home rather than replacing it, so Back always means "close this"
 * and the app is never more than one screen deep.
 *
 * Escape closes it, the body stops scrolling behind it, and focus moves into
 * the panel so a screen reader does not keep reading the page underneath.
 */
export function Sheet({ title, onClose, children, footer, className = "", labelledBy }) {
  const panel = useRef(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    panel.current?.focus();
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <>
      <div className="scrim scrim-sheet" onClick={onClose} />
      <section
        className={"sheet " + className}
        role="dialog"
        aria-modal="true"
        aria-label={labelledBy ? undefined : title}
        ref={panel}
        tabIndex={-1}
      >
        <span className="sheet-grab" aria-hidden="true" />
        <header className="sheet-head">
          {typeof title === "string" ? <h2>{title}</h2> : title}
          <button className="bar-btn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>
        <div className="sheet-body">{children}</div>
        {footer}
      </section>
    </>
  );
}

/** Horizontal section with a title and an optional "See all". */
export function Section({ title, more, onMore, children }) {
  return (
    <section className="sec">
      {(title || more) && (
        <div className="sec-head">
          <h2 className="sec-title">{title}</h2>
          {more && (
            <button className="sec-more" onClick={onMore}>
              {more}
            </button>
          )}
        </div>
      )}
      {children}
    </section>
  );
}
